import { useResizeObserver } from '@folklore/hooks';
import classNames from 'classnames';
import { parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useMemo, useCallback, createRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import * as AppPropTypes from '../../lib/PropTypes';

import Button from '../buttons/Button';
import Prize from './Prize';
import PrizeImage from './PrizeImage';
import Timer from './Timer';

import styles from '../../../styles/partials/prizes-selector.module.scss';

const propTypes = {
    className: PropTypes.string,
    prizes: AppPropTypes.prizes,
    selectPrize: PropTypes.func,
    onSelectTransitionStart: PropTypes.func,
    onSelectTransitionEnd: PropTypes.func,
};

const defaultProps = {
    className: null,
    prizes: null,
    selectPrize: null,
    onSelectTransitionStart: null,
    onSelectTransitionEnd: null,
};

function PrizesSelector({
    className,
    prizes,
    selectPrize,
    onSelectTransitionStart,
    onSelectTransitionEnd,
}) {
    const [selectedPrize, setSelectedPrize] = useState(
        (prizes || []).reduce(
            (selected, current) => (current.selectable ? selected : current),
            null,
        ),
    );
    const { id: selectedPrizeId = null } = selectedPrize || {};

    const autoSelectedPrize = useMemo(
        () => (prizes || []).find(({ selectedAt = null }) => selectedAt !== null) || null,
        [prizes],
    );

    const { selectedAt: autoSelectedPrizeSelectedAt = null } = autoSelectedPrize || {};
    const autoSelectedPrizeDate = useMemo(
        () => parseISO(autoSelectedPrizeSelectedAt),
        [autoSelectedPrizeSelectedAt],
    );

    const containerRef = useRef(null);
    const prizeRef = useRef(null);
    const selectedPrizeRef = useRef(null);

    const [prizeSelectedTransform, setPrizeSelectedTransform] = useState(null);

    const onSelectPrize = useCallback((prize) => {
        if (onSelectTransitionStart !== null) {
            onSelectTransitionStart();
        }

        setSelectedPrize(prize);
        if (selectPrize !== null) {
            selectPrize(prize);
        }
    }, []);

    const onTransitionEnd = useCallback(() => {
        if (onSelectTransitionEnd !== null) {
            onSelectTransitionEnd();
        }
    }, [selectedPrize, selectPrize]);

    const {
        ref: contentRef,
        entry: { contentRect = null },
    } = useResizeObserver();
    const { height: contentHeight = 0 } = contentRect || {};

    const {
        ref: selectedPrizeContainerRef,
        entry: { contentRect: selectedPrizeContainerRect = null },
    } = useResizeObserver();
    const { height: selectedPrizeContainerHeight = 0 } = selectedPrizeContainerRect || {};

    useEffect(() => {
        if (selectedPrize !== null) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const prizeRect = prizeRef.current.getBoundingClientRect();
            const selectedPrizeRect = selectedPrizeRef.current.getBoundingClientRect();
            const selectedPrizeContRect = selectedPrizeContainerRef.current.getBoundingClientRect();

            const scaleX = containerRect.width / prizeRect.width;
            const scaleY = selectedPrizeRect.height / prizeRect.height;
            const translateX = containerRect.x - prizeRect.x;
            const translateY =
                containerRect.y - prizeRect.y - (selectedPrizeContRect.y - selectedPrizeRect.y);

            setPrizeSelectedTransform(
                `translate(${translateX}px, ${translateY}px) scale(${scaleX}, ${scaleY})`,
            );
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [selectedPrize]);

    // React-Transition-Group
    const rtgNodes = useRef({});
    (prizes || []).forEach(({ id }) => {
        if ((rtgNodes.current[id] || null) === null) {
            rtgNodes.current[id] = createRef();
        }
    });

    const rtgClassNames = useMemo(
        () => ({
            enterActive: styles.enterActive,
            enterDone: styles.enterDone,
            exitActive: styles.exitActive,
            exitDone: styles.exitDone,
        }),
        [],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                    [styles.prizeSelected]: selectedPrize !== null,
                },
            ])}
            ref={containerRef}
            style={
                selectedPrize !== null
                    ? {
                          height:
                              prizeSelectedTransform === null
                                  ? contentHeight
                                  : selectedPrizeContainerHeight,
                      }
                    : null
            }
        >
            <div className={styles.background} />
            <div ref={contentRef} className={styles.content}>
                <div className={styles.inner}>
                    <div className={styles.title}>
                        <FormattedMessage
                            defaultMessage="Instant prize"
                            description="PrizeSelector title"
                        />
                    </div>
                    <div className={styles.subtitle}>
                        <FormattedMessage
                            defaultMessage="Congratulations! Which prize will it be today?{br}{br}Choose your prize:"
                            description="PrizeSelector subtitle"
                        />
                    </div>
                    <TransitionGroup className={styles.prizes}>
                        {(prizes || []).map((prize) => {
                            const { id, prize: prizeObject } = prize || {};
                            const { name } = prizeObject || {};
                            const prizeSelected = id === selectedPrizeId;
                            return (
                                <CSSTransition
                                    key={id}
                                    classNames={rtgClassNames}
                                    nodeRef={rtgNodes.current[id]}
                                    timeout={1250}
                                >
                                    <Button
                                        ref={rtgNodes.current[id]}
                                        onClick={() => {
                                            onSelectPrize(prize);
                                        }}
                                        className={classNames([
                                            styles.prize,
                                            {
                                                [styles.selected]: prizeSelected,
                                                [styles.unselected]: !prizeSelected,
                                            },
                                        ])}
                                    >
                                        <span
                                            className={styles.prizeContent}
                                            style={prizeSelected ? { zIndex: 1 } : null}
                                        >
                                            <span
                                                className={styles.prizeInner}
                                                style={
                                                    prizeSelected
                                                        ? { transform: prizeSelectedTransform }
                                                        : null
                                                }
                                            >
                                                <span
                                                    className={styles.prizeImageContainer}
                                                    ref={prizeSelected ? prizeRef : null}
                                                    style={
                                                        prizeSelected
                                                            ? {
                                                                  transform: 'rotateY(1turn)',
                                                                  borderRadius: 5,
                                                              }
                                                            : null
                                                    }
                                                >
                                                    <span
                                                        className={styles.prizeBackground}
                                                        onTransitionEnd={
                                                            prizeSelected ? onTransitionEnd : null
                                                        }
                                                    />
                                                    <span className={styles.prizeImageInner}>
                                                        <PrizeImage
                                                            className={styles.prizeImage}
                                                            prize={prize}
                                                        />
                                                    </span>
                                                </span>
                                            </span>
                                            <span className={styles.prizeName}>{name}</span>
                                        </span>
                                    </Button>
                                </CSSTransition>
                            );
                        })}
                    </TransitionGroup>
                </div>
                <Timer
                    className={styles.prizeSelectionTimer}
                    date={autoSelectedPrizeDate}
                    started={autoSelectedPrize !== null && selectedPrizeId === null}
                    onEnded={() => {
                        onSelectPrize(autoSelectedPrize);
                    }}
                />
                <div className={styles.note}>
                    <FormattedMessage
                        defaultMessage="Click on one of the prizes above. If no prize is chosen before the time runs out, the prize on the left will be selected automatically."
                        description="Selected prize note"
                    />
                </div>
            </div>
            <div className={styles.selectedPrizeContainer} ref={selectedPrizeContainerRef}>
                <div className={styles.selectedPrize}>
                    <h1 className={styles.selectedPrizeTitle}>
                        <FormattedMessage defaultMessage="Your prize" description="Title" />
                    </h1>
                    <Prize
                        className={styles.prizeCardBody}
                        prize={selectedPrize}
                        cardBodyRef={selectedPrizeRef}
                        withoutHeader
                        withAccountLink
                    />
                </div>
            </div>
        </div>
    );
}

PrizesSelector.propTypes = propTypes;
PrizesSelector.defaultProps = defaultProps;

export default PrizesSelector;
